import React, { useEffect, useRef, useState } from "react";
import { Divider, FormHelperText, Grid } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import CustomButton from "../../../components/Form/CustomButton";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Notification from "../../../components/Common/Notification";
import Helpers from "../../../helpers/helpers";
import { getAppearanceSettings, updateAppearanceSettings } from "../../../store/setting/settingApi";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
const DESKTOP_HINT = "Recommended: 1920 × 600 px";
const MOBILE_HINT = "Recommended: 768 × 400 px";

const ImageUploadField = ({
    title,
    hint,
    previewUrl,
    editable,
    onChoose,
    onRemove,
    errorMessage,
    inputId,
    fileInputRef,
}) => (
    <Grid item xs={12} md={6}>
        <div className="setting-form-section-header mb-2">{title}</div>
        <div className="setting-desc mb-2">{hint}</div>
        <div className="setting-desc mb-2">PNG, JPG, GIF, or WebP. Max 5MB.</div>
        {previewUrl && (
            <div
                className="mb-3"
                style={{
                    border: "1px solid #EDEDED",
                    borderRadius: 8,
                    overflow: "hidden",
                    maxHeight: 220,
                }}
            >
                <img
                    src={previewUrl}
                    alt={`${title} preview`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                />
            </div>
        )}
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            <CustomButton
                title="Choose image"
                press={() => editable && document.getElementById(inputId).click()}
                backgroundColor="#101010"
                textColor="#FFFFFF"
                borderStyle="1px solid #101010"
                disabled={!editable}
            />
            {previewUrl && (
                <CustomButton
                    title="Reset to default"
                    press={onRemove}
                    backgroundColor="white"
                    textColor="#101010"
                    borderStyle="1px solid #E3E8EE"
                    disabled={!editable}
                />
            )}
            <input
                type="file"
                id={inputId}
                ref={fileInputRef}
                style={{ display: "none" }}
                accept={ACCEPTED_TYPES.join(",")}
                onChange={(e) => onChoose(e.target.files[0])}
                disabled={!editable}
            />
        </div>
        {!editable && (
            <div className="setting-desc mt-2">You do not have permission to upload images.</div>
        )}
        {errorMessage && <FormHelperText sx={{ color: "red" }}>{errorMessage}</FormHelperText>}
    </Grid>
);

const AppearanceSetting = ({ userPermission }) => {
    const navigate = useNavigate();
    const desktopInputRef = useRef(null);
    const mobileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editable, setEditable] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [error, setError] = useState(null);

    const [desktopPreview, setDesktopPreview] = useState("");
    const [mobilePreview, setMobilePreview] = useState("");
    const [desktopPending, setDesktopPending] = useState(null);
    const [mobilePending, setMobilePending] = useState(null);
    const [resetDesktop, setResetDesktop] = useState(false);
    const [resetMobile, setResetMobile] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        msg: "",
        type: "success",
    });

    const loadSettings = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getAppearanceSettings();

            if (response?.code === "success") {
                setDesktopPreview(response.data.desktop_url);
                setMobilePreview(response.data.mobile_url);
                setDesktopPending(null);
                setMobilePending(null);
                setResetDesktop(false);
                setResetMobile(false);
            } else {
                setError(response?.message || "Failed to load appearance settings.");
            }
        } catch (loadError) {
            setError("Failed to load appearance settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userPermission?.length > 0) {
            userPermission?.includes("edit-setting") && setEditable(true);
            !userPermission?.includes("view-setting") && navigate("/not-authorized");
        }
    }, [userPermission, navigate]);

    useEffect(() => {
        loadSettings();

        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleFileSelect = (file, setPreview, setPending, inputRef) => {
        if (!file) {
            return;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
            setNotification({
                open: true,
                msg: "Only PNG, JPG, GIF, and WebP images are allowed.",
                type: "warning",
            });
            inputRef.current.value = "";
            return;
        }

        if (!Helpers.isFileSizeValid(file, 5)) {
            setNotification({
                open: true,
                msg: "File size must be less than 5MB.",
                type: "warning",
            });
            inputRef.current.value = "";
            return;
        }

        Helpers.getPreviewBase64(file, (result) => {
            setPreview(result);
            setPending(result);
        });

        inputRef.current.value = "";
    };

    const saveAppearance = async () => {
        if (!desktopPending && !mobilePending && !resetDesktop && !resetMobile) {
            setNotification({
                open: true,
                msg: "No changes to save.",
                type: "warning",
            });
            return;
        }

        setSaving(true);
        setError(null);

        const payload = {};

        if (resetDesktop) {
            payload.reset_desktop = true;
        } else if (desktopPending) {
            payload.desktop_image = desktopPending;
        }

        if (resetMobile) {
            payload.reset_mobile = true;
        } else if (mobilePending) {
            payload.mobile_image = mobilePending;
        }

        try {
            const response = await updateAppearanceSettings(payload);

            if (response?.code === "success") {
                setNotification({
                    open: true,
                    msg: response.warning || response.message || "Appearance saved successfully.",
                    type: response.warning ? "warning" : "success",
                });
                await loadSettings();
            } else if (response?.code === "invalid_field") {
                const imageError = response?.errors?.image?.[0];
                setError(imageError || "Invalid image upload.");
            } else {
                setError(response?.message || "Failed to save appearance settings.");
            }
        } catch (saveError) {
            setError("Failed to save appearance settings.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            {width <= 425 && editable && (
                <div className="mobile-button-container">
                    <CustomButton
                        title="Save Changes"
                        press={saveAppearance}
                        height={40}
                        backgroundColor="#101010"
                        textColor="white"
                        width="100%"
                        disabled={saving}
                    />
                </div>
            )}
            <div className="page-content">
                <LoadingDialog open={loading || saving} />
                <Container fluid>
                    <Notification notification={notification} setNotification={setNotification} />
                    <Breadcrumb
                        title="Appearance"
                        backRoute="/settings"
                        breadcrumbItems={[
                            { title: "Settings", link: "/settings" },
                            { title: "Appearance", link: "#" },
                        ]}
                    >
                        {editable && width > 425 && (
                            <CustomButton
                                title="Save Changes"
                                press={saveAppearance}
                                height={40}
                                backgroundColor="#101010"
                                textColor="white"
                                disabled={saving}
                            />
                        )}
                    </Breadcrumb>
                    <Card className="p-3">
                        <CardBody>
                            <div className="setting-form-section-header">HOMEPAGE SECTION BACKGROUNDS</div>
                            <Divider className="my-2 mb-4" />
                            <div className="setting-desc mb-4">
                                Uploads replace fixed files on the server: <code>solutions-bg-desktop.png</code> and{" "}
                                <code>solutions-bg-mobile.png</code> under <code>public/assets/images/background/</code>.
                                Both homepage sections (student portal and Ariba) use these images. Saving runs{" "}
                                <code>generate:page</code> immediately so the live storefront updates.
                            </div>
                            {error && (
                                <FormHelperText sx={{ color: "red", display: "block", mb: 2 }}>{error}</FormHelperText>
                            )}
                            {(desktopPending || mobilePending || resetDesktop || resetMobile) && (
                                <div className="setting-desc mb-3" style={{ color: "#ED1A3D" }}>
                                    You have unsaved changes. Click Save Changes to upload and refresh the storefront.
                                </div>
                            )}
                            <Grid container spacing={3}>
                                <ImageUploadField
                                    title="Desktop background"
                                    hint={DESKTOP_HINT}
                                    previewUrl={desktopPreview}
                                    editable={editable}
                                    inputId="appearance-desktop-upload"
                                    fileInputRef={desktopInputRef}
                                    onChoose={(file) => {
                                        setResetDesktop(false);
                                        handleFileSelect(file, setDesktopPreview, setDesktopPending, desktopInputRef);
                                    }}
                                    onRemove={() => {
                                        setResetDesktop(true);
                                        setDesktopPending(null);
                                    }}
                                />
                                <ImageUploadField
                                    title="Mobile background"
                                    hint={MOBILE_HINT}
                                    previewUrl={mobilePreview}
                                    editable={editable}
                                    inputId="appearance-mobile-upload"
                                    fileInputRef={mobileInputRef}
                                    onChoose={(file) => {
                                        setResetMobile(false);
                                        handleFileSelect(file, setMobilePreview, setMobilePending, mobileInputRef);
                                    }}
                                    onRemove={() => {
                                        setResetMobile(true);
                                        setMobilePending(null);
                                    }}
                                />
                            </Grid>
                            {editable && (
                                <div className="d-flex justify-content-end mt-4 pt-2">
                                    <CustomButton
                                        title="Save Changes"
                                        press={saveAppearance}
                                        height={40}
                                        backgroundColor="#101010"
                                        textColor="white"
                                        disabled={saving}
                                    />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

function mapStateToProps({ Login }) {
    return {
        userPermission: Login.permissionArry,
    };
}

export default connect(mapStateToProps)(AppearanceSetting);